import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, DownloadSimple, EnvelopeSimple, Ticket } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

const SYM = { gbp: "£", usd: "$", eur: "€", cad: "$", aud: "$", nzd: "$", inr: "₹", jpy: "¥" };
const money = (n, c) => `${SYM[(c || "gbp").toLowerCase()] ?? ""}${Number(n || 0).toFixed(2)}`;

export default function MemberReceipts() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/member/receipts").then(({ data }) => setData(data)).catch(() => setData({ receipts: [], total_paid: 0 })); }, []);

  const download = async (r) => {
    setBusy(r.id);
    try {
      const res = await api.get(`/member/receipts/${r.kind}/${r.id}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${r.number}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed"); }
    finally { setBusy(null); }
  };
  const email = async (r) => {
    setBusy(r.id);
    try { const { data } = await api.post(`/member/receipts/${r.kind}/${r.id}/email`); toast.success(`Receipt sent to ${data.to}`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!data) return <p className="tracker text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="member-receipts">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Receipts ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Your payment history.</h1>
          <p className="text-zinc-500 mt-2">Every class you've paid for and every pack you've bought — download a branded PDF or have it emailed to you.</p>
        </div>
        <div className="card-flat px-5 py-3 text-right" data-testid="member-receipts-total">
          <div className="tracker text-[9px] text-zinc-500">TOTAL PAID</div>
          <div className="font-display text-2xl">{money(data.total_paid, data.receipts[0]?.currency)}</div>
        </div>
      </div>

      {data.receipts.length === 0 ? (
        <div className="card-flat p-10 text-center" data-testid="member-receipts-empty">
          <Receipt size={28} weight="duotone" className="text-brand mx-auto mb-3" />
          <p className="font-display text-xl">No receipts yet.</p>
          <p className="text-zinc-500 text-sm mt-1">They'll appear here (and land in your inbox) the moment you pay for a class or pack.</p>
        </div>
      ) : (
        <div className="divide-y divide-white/5 border border-white/10" data-testid="member-receipts-list">
          {data.receipts.map((r) => (
            <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`member-receipt-${r.id}`}>
              <div className="min-w-0 flex items-start gap-3">
                <span className="mt-1 shrink-0 text-zinc-500">{r.kind === "pack" ? <Ticket size={18} weight="duotone" /> : <Receipt size={18} weight="duotone" />}</span>
                <div className="min-w-0">
                  <div className="font-display text-lg truncate">{r.title}</div>
                  <div className="text-xs text-zinc-400 truncate">{r.detail}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 tracker text-[9px] text-zinc-500">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rotate-45" style={{ background: r.business.accent }} /> {r.business.brand_name}</span>
                    <span>{r.number}</span><span>{r.date_label}</span>
                    <span className={r.method === "card" ? "text-emerald-400" : "text-zinc-400"}>{r.method_label.toUpperCase()}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="font-display text-xl" data-testid={`member-receipt-amount-${r.id}`}>{money(r.amount, r.currency)}</div>
                <button onClick={() => download(r)} disabled={busy === r.id} className="btn-ghost !py-1.5 !px-2 !text-[9px]" data-testid={`member-receipt-download-${r.id}`} title="Download PDF"><DownloadSimple size={12} /> PDF</button>
                <button onClick={() => email(r)} disabled={busy === r.id} className="btn-ghost !py-1.5 !px-2 !text-[9px]" data-testid={`member-receipt-email-${r.id}`} title="Email me this receipt"><EnvelopeSimple size={12} /> Email</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
